import { memo, useCallback } from 'react';
import {
  getSmoothStepPath,
  EdgeLabelRenderer,
  type EdgeProps,
  Position,
} from '@xyflow/react';
import type { Cardinality } from '@veodiagram/shared';

// ============= Edge Style by Cardinality =============

function getEdgeColor(cardinality: Cardinality | undefined): string {
  switch (cardinality) {
    case '1:1': return '#6366f1'; // indigo
    case '1:N': return '#22c55e'; // green
    case 'N:1': return '#f59e0b'; // amber
    case 'N:M': return '#ec4899'; // pink
    default:    return '#64748b'; // slate
  }
}

function isDashed(cardinality: Cardinality | undefined): boolean {
  return cardinality === 'N:M';
}

// ============= Crow's Foot Notation Helpers =============

type NotationType = 'one' | 'many';

function getSourceNotation(cardinality: Cardinality | undefined): NotationType {
  switch (cardinality) {
    case 'N:1':
    case 'N:M':
      return 'many';
    default:
      return 'one';
  }
}

function getTargetNotation(cardinality: Cardinality | undefined): NotationType {
  switch (cardinality) {
    case '1:N':
    case 'N:M':
      return 'many';
    default:
      return 'one';
  }
}

/**
 * Draw Crow's Foot notation at an edge endpoint.
 * position — which side of the node the edge connects to.
 * The markers extend into the edge (away from the node).
 */
function EdgeNotation({
  x, y, position, notation, color,
}: {
  x: number;
  y: number;
  position: Position;
  notation: NotationType;
  color: string;
}) {
  const isH = position === Position.Left || position === Position.Right;
  // +1 if edge goes right/down from node, -1 if left/up
  const dir = (position === Position.Right || position === Position.Bottom) ? 1 : -1;
  const BAR = 7;  // half-height of bars
  const GAP = 5;  // distance from endpoint to first mark
  const STEP = 5; // spacing between double bar

  if (notation === 'one') {
    // Two parallel perpendicular bars (mandatory one)
    if (isH) {
      const x1 = x + dir * GAP;
      const x2 = x1 + dir * STEP;
      return (
        <g>
          <line x1={x1} y1={y - BAR} x2={x1} y2={y + BAR} stroke={color} strokeWidth={1.5} />
          <line x1={x2} y1={y - BAR} x2={x2} y2={y + BAR} stroke={color} strokeWidth={1.5} />
        </g>
      );
    } else {
      const y1 = y + dir * GAP;
      const y2 = y1 + dir * STEP;
      return (
        <g>
          <line x1={x - BAR} y1={y1} x2={x + BAR} y2={y1} stroke={color} strokeWidth={1.5} />
          <line x1={x - BAR} y1={y2} x2={x + BAR} y2={y2} stroke={color} strokeWidth={1.5} />
        </g>
      );
    }
  } else {
    // Crow's foot (zero-or-many): 3 tines + circle
    if (isH) {
      const xFar = x + dir * (GAP + 6);
      const xNear = x + dir * GAP;
      const xCircle = x + dir * (GAP + 14);
      return (
        <g>
          {/* Three tines */}
          <line x1={x} y1={y} x2={xFar} y2={y - BAR} stroke={color} strokeWidth={1.5} />
          <line x1={x} y1={y} x2={xFar} y2={y}       stroke={color} strokeWidth={1.5} />
          <line x1={x} y1={y} x2={xFar} y2={y + BAR} stroke={color} strokeWidth={1.5} />
          {/* Crossbar at base of tines */}
          <line x1={xNear} y1={y - BAR} x2={xNear} y2={y + BAR} stroke={color} strokeWidth={1.5} />
          {/* Circle (optional) */}
          <circle cx={xCircle} cy={y} r={3.5} stroke={color} strokeWidth={1.5} fill="white" />
        </g>
      );
    } else {
      const yFar = y + dir * (GAP + 6);
      const yNear = y + dir * GAP;
      const yCircle = y + dir * (GAP + 14);
      return (
        <g>
          <line x1={x} y1={y} x2={x - BAR} y2={yFar} stroke={color} strokeWidth={1.5} />
          <line x1={x} y1={y} x2={x}       y2={yFar} stroke={color} strokeWidth={1.5} />
          <line x1={x} y1={y} x2={x + BAR} y2={yFar} stroke={color} strokeWidth={1.5} />
          <line x1={x - BAR} y1={yNear} x2={x + BAR} y2={yNear} stroke={color} strokeWidth={1.5} />
          <circle cx={x} cy={yCircle} r={3.5} stroke={color} strokeWidth={1.5} fill="white" />
        </g>
      );
    }
  }
}

// ============= Cardinality Badge =============

function cardinalityLabel(cardinality: Cardinality | undefined): string {
  switch (cardinality) {
    case '1:1': return '1:1';
    case '1:N': return '1:N';
    case 'N:1': return 'N:1';
    case 'N:M': return 'N:M';
    default:    return '';
  }
}

// ============= CrowsFootEdge Component =============

export interface CrowsFootEdgeData {
  cardinality?: Cardinality;
  fromColumn?: string;
  toColumn?: string;
  isRequired?: boolean;
  onDelete?: string;
  onUpdate?: string;
  onEdgeClick?: (edgeId: string) => void;
  [key: string]: unknown;
}

export const CrowsFootEdge = memo(function CrowsFootEdge({
  id,
  sourceX, sourceY,
  targetX, targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps) {
  const edgeData = (data || {}) as CrowsFootEdgeData;
  const cardinality = edgeData.cardinality;
  const color = getEdgeColor(cardinality);
  const dashed = isDashed(cardinality);

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 8,
  });

  const handleClick = useCallback(() => {
    if (edgeData.onEdgeClick) {
      edgeData.onEdgeClick(id);
    }
  }, [id, edgeData]);

  const strokeWidth = selected ? 2.5 : 1.5;
  const opacity = selected ? 1 : 0.8;

  return (
    <>
      {/* Invisible wide hit area for easy clicking */}
      <path
        d={edgePath}
        strokeWidth={12}
        stroke="transparent"
        fill="none"
        style={{ cursor: 'pointer' }}
        onClick={handleClick}
      />
      {/* Main edge path */}
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={dashed ? '5 4' : undefined}
        fill="none"
        opacity={opacity}
        style={{ cursor: 'pointer' }}
        onClick={handleClick}
      />

      {/* Selection highlight */}
      {selected && (
        <path
          d={edgePath}
          stroke={color}
          strokeWidth={4}
          fill="none"
          opacity={0.2}
        />
      )}

      {/* Crow's Foot notation at source end */}
      <EdgeNotation
        x={sourceX}
        y={sourceY}
        position={sourcePosition || Position.Right}
        notation={getSourceNotation(cardinality)}
        color={color}
      />

      {/* Crow's Foot notation at target end */}
      <EdgeNotation
        x={targetX}
        y={targetY}
        position={targetPosition || Position.Left}
        notation={getTargetNotation(cardinality)}
        color={color}
      />

      {/* Cardinality badge in the middle */}
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
            cursor: 'pointer',
          }}
          onClick={handleClick}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color,
              background: 'white',
              border: `1px solid ${color}`,
              borderRadius: 4,
              padding: '1px 4px',
              opacity: 0.9,
              whiteSpace: 'nowrap',
            }}
          >
            {cardinalityLabel(cardinality)}
          </span>
        </div>
      </EdgeLabelRenderer>
    </>
  );
});

CrowsFootEdge.displayName = 'CrowsFootEdge';
